import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import useAccessToken from "../Custom/useAccessToken.tsx";
import { getUserPermissions } from "../Services/permissionService.ts";
import {
  FeaturePermission,
  PermissionUser,
  UserGroup,
  UserPermissions,
} from "../Models/Permission.ts";
import {
  PermissionActionName,
  PermissionFeature,
} from "../Constants/Permissions.ts";

interface PermissionContextProps {
  // Raw permission payload returned by the API.
  permissionData: UserPermissions | null;
  user: PermissionUser | null;
  permissions: FeaturePermission[];
  groups: UserGroup[];
  loading: boolean;
  error: Error | null;
  // Re-fetch permissions on demand.
  refreshPermissions: () => Promise<void>;
  // Specific permission helpers.
  hasPermission: (feature: PermissionFeature, action: PermissionActionName,) => boolean;
  checkServicelinePermission: (feature: PermissionFeature, action: PermissionActionName, serviceLineId: string) => any;
  getFeaturePermissions: (feature: PermissionFeature,) => FeaturePermission | undefined;
  hasRole: (role: string) => boolean;
  isInGroup: (groupKey: string) => boolean;
  getGroupKeysByFeature: (feature: PermissionFeature) => string[];
}

const PermissionContext = createContext<PermissionContextProps | undefined>(
  undefined
);

const normalize = (value: string) => value?.trim().toLowerCase();

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { accessToken } = useAccessToken();
  const [permissionData, setPermissionData] = useState<UserPermissions | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserPermissions(accessToken);
      setPermissionData(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  // Fetch permissions once the user is logged in and a token is available.
  useEffect(() => {
    if (accessToken) {
      loadPermissions();
    }
  }, [accessToken, loadPermissions]);

  const permissions = permissionData?.permissions ?? [];
  const user = permissionData?.user ?? null;
  const groups = user?.groups ?? [];

  const getFeaturePermissions = useCallback(
    (feature: string): FeaturePermission | undefined =>
      permissions.find((p) => normalize(p.feature) === normalize(feature)),
    [permissions]
  );

  const hasPermission = useCallback(
    (feature: string, action: string): any => {
      const featurePermission = getFeaturePermissions(feature);
      if (!featurePermission) {
        return null;
      }
      const actionPermission = featurePermission.actions.find(
        (a) => normalize(a.name) === normalize(action)
      );
      if (!actionPermission) {
        return null;
      }
      return featurePermission.actions.some(
        (a) => normalize(a.name) === normalize(action) && a.allowed
      );
    },
    [getFeaturePermissions]
  );

  const checkServicelinePermission = useCallback(
    (feature: string, action: string, serviceLineId: string): any => {
      const featurePermission = getFeaturePermissions(feature);
      if (!featurePermission) {
        return null;
      }
      return featurePermission.actions.some(
        (a) => normalize(a.name) === normalize(action) && a.allowed && a.groups?.some((g) => g.serviceLineId === serviceLineId)
      );
    },
    [getFeaturePermissions]
  );

  const hasRole = useCallback(
    (role: string): boolean => normalize(user?.role ?? "") === normalize(role),
    [user]
  );

  const isInGroup = useCallback(
    (groupKey: string): boolean =>
      groups.some((g) => normalize(g.key) === normalize(groupKey)),
    [groups]
  );

  const getGroupKeysByFeature = useCallback(
      (feature: PermissionFeature): string[] =>
          groups
              .filter((g) => normalize(g.feature) === normalize(feature))
              .map((g) => g.key),
    [groups]
  );

  const value = useMemo<PermissionContextProps>(
    () => ({
      permissionData,
      user,
      permissions,
      groups,
      loading,
      error,
      refreshPermissions: loadPermissions,
      hasPermission,
      checkServicelinePermission,
      getFeaturePermissions,
      hasRole,
      isInGroup,
      getGroupKeysByFeature
    }),
    [
      permissionData,
      user,
      permissions,
      groups,
      loading,
      error,
      loadPermissions,
      hasPermission,
      checkServicelinePermission,
      getFeaturePermissions,
      hasRole,
      isInGroup,
      getGroupKeysByFeature
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};
